import React from 'react';
import PropTypes from 'prop-types';
import Avatar from '../avatar/avatar';
import CopyText from '../copy_text';

function buildInitials(fullName) {
  return fullName
    .split(' ')
    .filter((part) => part.trim())
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function employmentString(contact) {
  const { titleAtWorkplace, workplaceCompanyName } = contact;
  if (titleAtWorkplace && workplaceCompanyName) {
    // title_at_workplace may already include "at <company>" — avoid duplication
    if (titleAtWorkplace.toLowerCase().includes(workplaceCompanyName.toLowerCase())) {
      return titleAtWorkplace;
    }
    return `${titleAtWorkplace} at ${workplaceCompanyName}`;
  }
  return titleAtWorkplace || workplaceCompanyName || '';
}

function ContactRow({
  contact, hashtags, pitchCount, selected, onSelect,
}) {
  const employment = employmentString(contact);
  const profileUrl = contact.v1ProfilePath || null;
  const visibleHashtags = hashtags.slice(0, 3);

  return (
    <tr>
      <td className="ps-3 py-3">
        <div className="d-flex align-items-center gap-2">
          <input
            type="checkbox"
            className="form-check-input mt-0 my-contacts-checkbox"
            checked={selected}
            onChange={onSelect}
            aria-label={`Select contact ${contact.name}`}
          />
          <Avatar
            avatarLetters={buildInitials(contact.name)}
            avatarUrl={contact.avatarUrl}
            sizeClass="avatar-sm"
          />
          <div className="d-flex flex-column lh-sm">
            {profileUrl ? (
              <a href={profileUrl} className="fw-semibold text-decoration-none text-primary fs-5">{contact.name}</a>
            ) : (
              <span className="fw-semibold text-primary fs-5">{contact.name}</span>
            )}
            {employment && (
              <small className="text-muted mt-1 fs-6">{employment}</small>
            )}
          </div>
        </div>
      </td>
      <td className="py-3">
        <span>{contact.typeOfProfile}</span>
      </td>
      <td className="d-none d-md-table-cell py-3">
        <div className="d-flex flex-wrap gap-1">
          {visibleHashtags.map((h) => (
            <a
              key={h.slug}
              href={`/hashtags/${h.slug}`}
              className="badge rounded-pill bg-light text-dark border text-decoration-none small"
            >
              {h.name}
            </a>
          ))}
        </div>
      </td>
      <td className="text-end py-3">{pitchCount}</td>
      <td className="text-end py-3">{contact.numMessages}</td>
      <td className="pe-3 py-3">
        <div className="d-flex justify-content-end align-items-center gap-1">
          {profileUrl && (
            <CopyText
              text={`${window.location.origin}${profileUrl}`}
              tooltipText="Copied!"
              uniqueId={`copy-profile-link-${contact.profileId}`}
              buttonClassName="btn btn-sm btn-outline-secondary"
              iconClassName="fa-regular fa-copy"
              hoverTooltipText="Copy profile link"
            />
          )}
          {contact.conversationPath && (
            <a
              href={contact.conversationPath}
              className="btn btn-sm btn-outline-secondary"
            >
              Message
            </a>
          )}
          {profileUrl && (
            <a
              href={profileUrl}
              className="btn btn-sm btn-outline-secondary"
            >
              Profile
            </a>
          )}
        </div>
      </td>
    </tr>
  );
}

ContactRow.propTypes = {
  contact: PropTypes.shape({
    profileId: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    v1ProfilePath: PropTypes.string,
    avatarUrl: PropTypes.string,
    titleAtWorkplace: PropTypes.string,
    workplaceCompanyName: PropTypes.string,
    rawTypeOfProfile: PropTypes.string,
    typeOfProfile: PropTypes.string,
    numMessages: PropTypes.number,
    conversationId: PropTypes.string,
    conversationPath: PropTypes.string,
  }).isRequired,
  hashtags: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
  })),
  pitchCount: PropTypes.number,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

ContactRow.defaultProps = {
  hashtags: [],
  pitchCount: 0,
  selected: false,
};

export default ContactRow;
